import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend,
} from 'recharts';
import { motion, AnimatePresence } from 'motion/react';
import {
  Package, Wallet, Landmark, TrendingUp, DollarSign, CalendarDays,
  Trophy, History, PieChart as PieChartIcon, Target, Lock, BarChart3, Activity,
  Heart, Search,
} from 'lucide-react';
import { getSoldRows, getUnsoldRows, getIncludedSold, getTotalProfit, getLivePrice, getTradeLock, TRADE_LOCK_DAYS } from './helpers';
import { rowName, dayOf, usePortfolioVsMarket } from './priceArchive';
import ItemDetailModal from './ItemDetailModal';
import { findSkinImage } from './skinIndex';

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Categorical palette — validated colorblind-safe set, fixed order (dataviz skill).
const CATEGORY_COLORS = ['#3987e5','#199e70','#c98500','#008300','#9085e9','#e66767','#d55181','#d95926'];

// Chart chrome tokens (recessive axes/grid, readable muted ink).
const AXIS_TICK = '#9196aa';
const GRID      = 'rgba(255,255,255,0.06)';
const SURFACE   = '#13151f';   // var(--panel) — gap/ring color between marks

function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}

// Watch key is skin+condition (never assetId) so a heart follows the skin,
// not the individual item, and survives that item being sold.
function holdingKey(row) {
  return `${row.skin?.value}|${row.condition?.value || ''}`;
}


// ── Animated count-up ────────────────────────────────────────
function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const prevRef = useRef(0);
  useEffect(() => {
    const from = prevRef.current;
    const to   = Number.isFinite(target) ? target : 0;
    prevRef.current = to;
    if (from === to) { setVal(to); return; }
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function Money({ value, signed = false, decimals = 2 }) {
  const v = useCountUp(value ?? 0);
  const sign = signed && v >= 0 ? '+' : v < 0 ? '−' : '';
  return <>{sign}${Math.abs(v).toFixed(decimals)}</>;
}

function Count({ value }) {
  const v = useCountUp(value ?? 0, 700);
  return <>{Math.round(v)}</>;
}

// ── Cylinder bar ─────────────────────────────────────────────
// A cylinder on its side from three boxes: a full ellipse cap on the near
// end, a body with only its top and bottom edges outlined, and a far cap
// clipped to its right half. The capsules run left to right in one row, each
// as long as its share of the total, so the row reads as everything invested
// and each segment as one category's slice of it.
const CYL_BAR_H = 56;   // cylinder diameter
const CYL_CAP_W = 28;   // horizontal extent of the ellipse caps

function CylinderColumn({ fill, label, value, pct, delay }) {
  return (
    <div className="ov3-cyl-col" style={{ flexGrow: Math.max(pct, 0.001) }}>
      <div className="ov3-cyl-value">${value.toFixed(0)}</div>
      <motion.div
        className="ov3-cyl-shape"
        data-tooltip={`${label} · $${value.toFixed(2)} · ${pct.toFixed(0)}%`}
        style={{ height: CYL_BAR_H, '--ov3-cap-w': `${CYL_CAP_W}px` }}
        initial={{ width: '12%', opacity: 0 }}
        animate={{ width: '100%', opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      >
        <span className="ov3-cyl-body" style={{ background: fill }} />
        <span className="ov3-cyl-cap far" style={{ background: fill }} />
        <span className="ov3-cyl-cap near" style={{ background: fill }} />
      </motion.div>
      <div className="ov3-cyl-label">{label}</div>
    </div>
  );
}

// ── Tooltips ─────────────────────────────────────────────────
const ProfitTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="custom-tooltip">
      <div className="ct-label">{label}{p.count ? ` · ${p.count} flips` : ''}</div>
      <div className={`ct-value ${p.profit < 0 ? 'red' : ''}`}>
        {p.profit >= 0 ? '+' : ''}${p.profit.toFixed(2)}
      </div>
    </div>
  );
};

// ── Entrance animation variants ──────────────────────────────
const staggerGrid = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const riseIn = {
  hidden: { opacity: 0, y: 16 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// ── Stat card ────────────────────────────────────────────────
function StatCard({ icon: Icon, tone = 'neutral', label, children, sub, subColor }) {
  return (
    <motion.div variants={riseIn} className={`ov3-stat-card tone-${tone}`}>
      <div className="ov3-stat-top">
        <span className="ov3-stat-label">{label}</span>
        <span className="ov3-icon-chip"><Icon size={16} strokeWidth={2.2} /></span>
      </div>
      <div className="ov3-stat-value">{children}</div>
      {sub && <div className="ov3-stat-sub" style={subColor ? { color: subColor } : undefined}>{sub}</div>}
    </motion.div>
  );
}

// ── Win-rate ring ────────────────────────────────────────────
function WinRing({ pct }) {
  const R = 30, C = 2 * Math.PI * R;
  const clamped = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="ov3-winring">
      <svg width="84" height="84" viewBox="0 0 84 84">
        <circle cx="42" cy="42" r={R} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="7" />
        <motion.circle
          cx="42" cy="42" r={R} fill="none"
          stroke="var(--green)" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={C}
          initial={{ strokeDashoffset: C }}
          animate={{ strokeDashoffset: C * (1 - clamped / 100) }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
          transform="rotate(-90 42 42)"
        />
      </svg>
      <div className="ov3-winring-label">
        <strong><Count value={clamped} />%</strong>
        <span>win rate</span>
      </div>
    </div>
  );
}

// ── Horizontal gauge ─────────────────────────────────────────
// `display` is the headline figure and `caption` spells out the raw numbers
// behind it, so the dot on the track never has to be read on its own.
function MeterBar({ name, value, display, color, low, high, caption }) {
  return (
    <div className="ov3-meter">
      <div className="ov3-meter-top">
        <span className="ov3-meter-name">{name}</span>
        <span className="ov3-meter-value" style={{ color }}>{display}</span>
      </div>
      <div className="ov3-meter-track">
        <motion.div
          className="ov3-meter-dot"
          style={{ background: color }}
          initial={{ left: '50%' }}
          animate={{ left: `${value}%` }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
        />
      </div>
      <div className="ov3-meter-labels">
        <span>{low}</span>
        <span>{high}</span>
      </div>
      {caption && <div className="ov3-meter-caption">{caption}</div>}
    </div>
  );
}

export default function OverviewTab3({ rows, settings, livePrices, csfloatPrices, skinsData = [], inventoryMap, watchedKeys, onToggleWatch }) {
  const [detail, setDetail]               = useState(null);   // the holding whose modal is open
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [chartYear, setChartYear]         = useState(new Date().getFullYear());
  const [showBars, setShowBars]           = useState(true);
  const [showArea, setShowArea]           = useState(false);
  const [holdingsQuery, setHoldingsQuery] = useState('');
  const [favOnly, setFavOnly]             = useState(false);
  const [hoveredCat, setHoveredCat]       = useState(null);

  const isExcluded = useCallback(r => getItemTypes(r).some(t => {
    const def = settings?.itemTypes?.find(it => it.value === t.value);
    return def?.overrideProfitLoss === true;
  }), [settings?.itemTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableYears = useMemo(() => {
    const years = new Set();
    rows?.forEach(r => { const y = new Date(r.timestamp).getFullYear(); if (y) years.add(y); });
    return [...years].sort((a, b) => b - a);
  }, [rows]);

  // ── Stats ──────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!rows?.length) return {};
    const sold   = getSoldRows(rows);
    const unsold = getUnsoldRows(rows);

    const includedSold  = getIncludedSold(rows, isExcluded);
    const totalProfit   = getTotalProfit(rows, isExcluded);
    const totalSoldCost = includedSold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    const unsoldCost    = unsold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    const itemCount     = rows.filter(r => r.skin?.value).length;

    let unrealizedPnl = 0;
    let marketValue   = 0;
    let gainers = 0, losers = 0;
    unsold.forEach(r => {
      const lp  = getLivePrice(livePrices, r);
      const buy = parseFloat(r.buyPrice) || 0;
      if (lp) {
        const market = lp.starting_at?.price || 0;
        marketValue   += market;
        unrealizedPnl += market - buy;
        if (market >= buy) gainers++; else losers++;
      } else {
        marketValue += buy;
      }
    });

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const profit30d = includedSold
      .filter(r => new Date(r.timestamp) >= cutoff)
      .reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);

    const realizedRoi   = totalSoldCost > 0 ? (totalProfit / totalSoldCost * 100) : null;
    const unrealizedRoi = unsoldCost    > 0 ? (unrealizedPnl / unsoldCost * 100) : null;

    // Trade lock queue
    let lockedCost = 0, lockedCount = 0, unlock7 = 0;
    const in7 = new Date();
    in7.setDate(in7.getDate() + 7);
    unsold.forEach(r => {
      const inv = r.assetId ? inventoryMap?.get(r.assetId) : null;
      const { locked, until } = getTradeLock(r, inv);
      if (!locked) return;
      lockedCost  += parseFloat(r.buyPrice) || 0;
      lockedCount += 1;
      if (until && until <= in7) unlock7 += 1;
    });

    return { totalProfit, unsoldCost, marketValue, unrealizedPnl, itemCount, profit30d,
      soldCount: sold.length, unsoldCount: unsold.length, realizedRoi, unrealizedRoi,
      gainers, losers, lockedCost, lockedCount, unlock7 };
  }, [rows, livePrices, isExcluded, inventoryMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Monthly Profit ─────────────────────────────────────────
  const monthlyData = useMemo(() => {
    const months = MONTH_LABELS.map((label, i) => ({ label, month: i + 1, profit: 0, count: 0 }));
    rows?.filter(r => parseFloat(r.sellPrice) > 0 && !isExcluded(r)).forEach(r => {
      const d = new Date(r.timestamp);
      if (d.getFullYear() === chartYear) {
        const m = months[d.getMonth()];
        m.profit += parseFloat(r.profit) || 0;
        m.count  += 1;
      }
    });
    return months.map(m => ({ ...m, profit: parseFloat(m.profit.toFixed(2)) }));
  }, [rows, chartYear, isExcluded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flips table month defaults to the current month when nothing's selected.
  const flipsMonth = selectedMonth ?? (new Date().getMonth() + 1);

  const monthFlips = useMemo(() => {
    if (!rows) return [];
    return rows
      .filter(r => {
        const d = new Date(r.timestamp);
        return d.getFullYear() === chartYear && d.getMonth() + 1 === flipsMonth
          && parseFloat(r.sellPrice) > 0;
      })
      .sort((a, b) => parseFloat(b.profit) - parseFloat(a.profit));
  }, [rows, flipsMonth, chartYear]);

  const monthNet = useMemo(() => monthFlips.reduce((s, r) => s + (parseFloat(r.profit) || 0), 0), [monthFlips]);

  // ── Portfolio Mix ──────────────────────────────────────────
  const categoryData = useMemo(() => {
    if (!rows) return [];
    const liveRows = rows.filter(r => r.skin?.value && parseFloat(r.buyPrice) > 0);
    const typed = (settings?.itemTypes || [])
      .filter(t => t.value !== 'Normal')
      .map(type => {
        const typeRows = liveRows.filter(r => getItemTypes(r).some(t => t.value === type.value));
        const value = typeRows.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
        return { name: type.label, value: parseFloat(value.toFixed(2)), color: type.color, count: typeRows.length };
      })
      .filter(d => d.value > 0);

    const uncategorized = liveRows.filter(r => getItemTypes(r).length === 0);
    const uncatValue = uncategorized.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    if (uncatValue > 0) {
      typed.push({ name: 'Uncategorized', value: parseFloat(uncatValue.toFixed(2)), color: '#6b7280', count: uncategorized.length });
    }
    return typed;
  }, [rows, settings]);

  const catColors = useMemo(
    () => categoryData.map((d, i) => d.color || CATEGORY_COLORS[i % CATEGORY_COLORS.length]),
    [categoryData],
  );
  const totalCat = useMemo(() => categoryData.reduce((s, d) => s + d.value, 0), [categoryData]);

  // ── Top Holdings ───────────────────────────────────────────
  const allHoldings = useMemo(() => {
    if (!rows) return [];
    return getUnsoldRows(rows)
      // getUnsoldRows, not a hand-rolled sellPrice test: an item traded away
      // keeps an empty sellPrice forever and would still be listed as a holding.
      .filter(r => parseFloat(r.buyPrice) > 0)
      .sort((a, b) => parseFloat(b.buyPrice) - parseFloat(a.buyPrice));
  }, [rows]);

  const topItems = useMemo(() => {
    const q = holdingsQuery.trim().toLowerCase();
    return allHoldings
      .filter(r => {
        if (favOnly && !watchedKeys?.has(holdingKey(r))) return false;
        if (q && !r.skin?.value?.toLowerCase().includes(q)) return false;
        return true;
      })
      .slice(0, 9);
  }, [allHoldings, holdingsQuery, favOnly, watchedKeys]);

  // ── Recent Activity ────────────────────────────────────────
  const recent = useMemo(() => {
    if (!rows) return [];
    return [...rows]
      .filter(r => r.skin?.value)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 6);
  }, [rows]);

  // ── Trading Performance ────────────────────────────────────
  const perf = useMemo(() => {
    const sold = getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value);
    if (!sold.length) return null;
    const profits = sold.map(r => parseFloat(r.profit) || 0);
    const wins    = profits.filter(p => p > 0).length;
    let best = sold[0], worst = sold[0];
    sold.forEach(r => {
      const p = parseFloat(r.profit) || 0;
      if (p > (parseFloat(best.profit)  || 0)) best  = r;
      if (p < (parseFloat(worst.profit) || 0)) worst = r;
    });
    return {
      count: sold.length,
      wins,
      winRate: wins / sold.length * 100,
      avg: profits.reduce((s, p) => s + p, 0) / sold.length,
      best,
      worst,
    };
  }, [rows, isExcluded]);

  // ── Health checks ──────────────────────────────────────────
  // Three questions about your own trading, not about the CS2 market. Each
  // meter ships with a caption naming the raw numbers it came from.
  // ── You against the market ────────────────────────────────
  // Every other figure on this page measures you against yourself. This one
  // measures the holdings against what simply owning skins would have done
  // over the exact same months — the archive prices each item on the day it
  // was bought, and the chain-linked index supplies the benchmark.
  const marketPoints = useMemo(() => (rows || [])
    .filter(r => r.skin?.value
      && !(parseFloat(r.sellPrice) > 0)
      && !(r.tradeId || r.tradedFor?.length > 0)
      && !isExcluded(r))
    .map((r, i) => ({ key: `hold${i}`, name: rowName(r), date: dayOf(r.timestamp) })),
    [rows, isExcluded]);
  const vsMarket = usePortfolioVsMarket(marketPoints, csfloatPrices);

  const meters = useMemo(() => {
    const col   = (v) => v < 40 ? 'var(--red)' : v < 60 ? 'var(--amber)' : 'var(--green)';
    const money = (v) => `${v < 0 ? '−' : ''}$${Math.abs(v).toFixed(2)}`;

    // 1. Last 30 days measured against a typical month of realized profit.
    const monthTotals = {};
    (rows || []).filter(r => parseFloat(r.sellPrice) > 0 && !isExcluded(r)).forEach(r => {
      const d = new Date(r.timestamp);
      const k = `${d.getFullYear()}-${d.getMonth()}`;
      monthTotals[k] = (monthTotals[k] || 0) + (parseFloat(r.profit) || 0);
    });
    const abs    = Object.values(monthTotals).map(Math.abs).filter(v => v > 0);
    const avgAbs = abs.length ? abs.reduce((s, v) => s + v, 0) / abs.length : 0;
    const p30    = stats.profit30d ?? 0;
    const momentum = Math.round(50 + 50 * Math.max(-1, Math.min(1, p30 / (avgAbs || 1))));

    // 2. Share of the items you hold that are currently above their buy price.
    const gainers = stats.gainers ?? 0;
    const losers  = stats.losers ?? 0;
    const known   = gainers + losers;
    const gainPct = known ? Math.round(gainers / known * 100) : 50;

    // 3. Share of completed sales that made money.
    const winRate = perf ? Math.round(perf.winRate) : 50;

    return [
      {
        name: 'Recent form',
        value: momentum,
        display: avgAbs > 0 ? `${momentum}/100` : 'n/a',
        color: col(momentum),
        low: 'Slower than usual',
        high: 'Better than usual',
        caption: avgAbs > 0
          ? `You banked ${money(p30)} in the last 30 days. A typical month for you is ${money(avgAbs)}.`
          : 'Not enough months of sales yet to compare against.',
      },
      {
        name: 'Holdings in profit',
        value: gainPct,
        display: known ? `${gainPct}%` : 'n/a',
        color: col(gainPct),
        low: 'Most are down',
        high: 'Most are up',
        caption: known
          ? `${gainers} of the ${known} items you hold are worth more than you paid.`
          : 'Waiting on live prices for the items you hold.',
      },
      {
        name: 'Win rate',
        value: winRate,
        display: perf ? `${winRate}%` : 'n/a',
        color: col(winRate),
        low: 'Mostly losses',
        high: 'Mostly wins',
        caption: perf
          ? `${perf.wins} of your ${perf.count} completed sales made money.`
          : 'No completed sales yet.',
      },
      // Only when the archive priced something. Without it this meter would be
      // a permanent n/a on a page that already has three real ones.
      ...(vsMarket ? [{
        name: 'You vs the market',
        // Centred on 50 = you matched the market. Twenty percentage points
        // either way saturates the bar; beyond that the exact position stops
        // meaning anything and the caption carries the number.
        value: Math.round(50 + 50 * Math.max(-1, Math.min(1, (vsMarket.yourPct - vsMarket.marketPct) / 20))),
        display: `${vsMarket.yourPct - vsMarket.marketPct >= 0 ? '+' : '−'}${Math.abs(vsMarket.yourPct - vsMarket.marketPct).toFixed(1)}pp`,
        color: col(50 + 50 * Math.max(-1, Math.min(1, (vsMarket.yourPct - vsMarket.marketPct) / 20))),
        low: 'Market did better',
        high: 'You did better',
        caption: `Your ${vsMarket.n} priced holding${vsMarket.n === 1 ? ' is' : 's are'} ${
          vsMarket.yourPct >= 0 ? 'up' : 'down'} ${Math.abs(vsMarket.yourPct).toFixed(1)}% since you bought. `
          + `The market moved ${vsMarket.marketPct >= 0 ? 'up' : 'down'} ${Math.abs(vsMarket.marketPct).toFixed(1)}% over the same stretch.`,
      }] : []),
    ];
  }, [rows, isExcluded, stats.profit30d, stats.gainers, stats.losers, perf, vsMarket]);

  const portfolioHistory = useMemo(() => settings?.portfolioHistory || [], [settings]);

  const profitColor = settings?.profitTextColor || 'var(--green)';
  const lossColor   = settings?.lossTextColor   || 'var(--red)';
  const valueColor  = (v) => parseFloat(v) >= 0 ? profitColor : lossColor;

  const noData = !rows?.filter(r => r.skin?.value).length;
  if (noData) {
    return (
      <div className="page-content">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ov3-empty">
          <PieChartIcon size={28} strokeWidth={1.6} />
          <div className="ov3-empty-title">No data yet</div>
          <div className="ov3-empty-sub">Add some items in the Tracker tab to see your overview.</div>
        </motion.div>
      </div>
    );
  }

  const bestP  = perf ? parseFloat(perf.best.profit)  || 0 : 0;
  const worstP = perf ? parseFloat(perf.worst.profit) || 0 : 0;

  return (
    <motion.div className="page-content" variants={staggerGrid} initial="hidden" animate="show">

      {/* ── Stat Cards ─────────────────────────────────── */}
      <motion.div className="ov3-stat-grid" variants={staggerGrid}>
        <StatCard icon={Package} label="Total Items"
          sub={`${stats.soldCount ?? 0} sold · ${stats.unsoldCount ?? 0} holding`}>
          <Count value={stats.itemCount ?? 0} />
        </StatCard>
        <StatCard icon={Wallet} tone="amber" label="Total Invested"
          sub={`What the ${stats.unsoldCount ?? 0} items you hold cost you`}>
          <Money value={stats.unsoldCost ?? 0} />
        </StatCard>
        <StatCard icon={Landmark} tone="indigo" label="Current Market Value"
          sub="What they're worth at Buff163 prices">
          <Money value={stats.marketValue ?? 0} />
        </StatCard>
        <StatCard icon={TrendingUp}
          tone={(stats.unrealizedPnl ?? 0) >= 0 ? 'green' : 'red'}
          label="If You Sold Today"
          sub={stats.unrealizedRoi != null
            ? `${stats.unrealizedRoi >= 0 ? '+' : ''}${stats.unrealizedRoi.toFixed(1)}% vs. what you paid`
            : undefined}
          subColor={stats.unrealizedRoi != null ? valueColor(stats.unrealizedRoi) : undefined}>
          <span style={{ color: valueColor(stats.unrealizedPnl ?? 0) }}>
            <Money value={stats.unrealizedPnl ?? 0} signed />
          </span>
        </StatCard>
        <StatCard icon={DollarSign}
          tone={(stats.totalProfit ?? 0) >= 0 ? 'green' : 'red'}
          label="Profit Banked"
          sub={stats.realizedRoi != null
            ? `${stats.realizedRoi >= 0 ? '+' : ''}${stats.realizedRoi.toFixed(1)}% return on items sold`
            : undefined}
          subColor={stats.realizedRoi != null ? valueColor(stats.realizedRoi) : undefined}>
          <span style={{ color: valueColor(stats.totalProfit ?? 0) }}>
            <Money value={stats.totalProfit ?? 0} signed />
          </span>
        </StatCard>
        <StatCard icon={CalendarDays}
          tone={(stats.profit30d ?? 0) >= 0 ? 'green' : 'red'}
          label="30-Day Profit"
          sub="Realized, last 30 days">
          <span style={{ color: valueColor(stats.profit30d ?? 0) }}>
            <Money value={stats.profit30d ?? 0} signed />
          </span>
        </StatCard>
      </motion.div>

      <div className="overview-grid" style={{ marginTop: 12 }}>

        {/* ── Monthly Profit Bars ───────────────────────── */}
        <motion.div className="panel span-2" variants={riseIn}>
          <div className="panel-header">
            <div>
              <div className="panel-title">Monthly Profit</div>
              <div className="panel-subtitle">Realized profit by month · click a bar or point for details</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="ov3-seg">
                <button
                  className={`ov3-seg-btn ${showBars ? 'active' : ''}`}
                  onClick={() => setShowBars(v => (v && !showArea) ? v : !v)}
                  title={showBars ? 'Hide bars' : 'Show bars'}
                >
                  <BarChart3 size={16} strokeWidth={2.2} />
                </button>
                <button
                  className={`ov3-seg-btn ${showArea ? 'active' : ''}`}
                  onClick={() => setShowArea(v => (v && !showBars) ? v : !v)}
                  title={showArea ? 'Hide area' : 'Show area'}
                >
                  <Activity size={16} strokeWidth={2.2} />
                </button>
              </div>
              <div className="ov3-year-row">
                {availableYears.map(y => (
                  <button key={y} className={`ov3-year-btn ${chartYear === y ? 'active' : ''}`}
                    onClick={() => { setChartYear(y); setSelectedMonth(null); }}>
                    {y}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="chart-shell" style={{ padding: '18px 12px 6px' }}>
            <ResponsiveContainer width="100%" height={236}>
              <ComposedChart data={monthlyData} margin={{ top: 10, right: 18, left: 0, bottom: 0 }} barCategoryGap="28%">
                <defs>
                  <linearGradient id="ov3BarPos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#4ade80" />
                    <stop offset="100%" stopColor="#15803d" />
                  </linearGradient>
                  <linearGradient id="ov3BarNeg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#b91c1c" />
                    <stop offset="100%" stopColor="#f87171" />
                  </linearGradient>
                  <linearGradient id="ov3AreaProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#4ade80" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#4ade80" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={GRID} strokeDasharray={showBars ? undefined : '3 3'} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: AXIS_TICK, fontSize: 14.5 }} axisLine={false} tickLine={false} dy={4} />
                <YAxis tick={{ fill: AXIS_TICK, fontSize: 14.5 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={52} />
                <Tooltip content={<ProfitTooltip />} cursor={{ fill: 'rgba(255,255,255,0.045)', stroke: 'rgba(255,255,255,0.14)', strokeWidth: 1 }} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.18)" strokeWidth={1} />
                {showArea && (
                  <Area
                    type="monotone"
                    dataKey="profit"
                    stroke="#4ade80"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#ov3AreaProfit)"
                    dot={false}
                    activeDot={{
                      fill: '#4ade80', stroke: SURFACE, strokeWidth: 2, r: 5, cursor: 'pointer',
                      onClick: (_, e) => {
                        const m = e?.payload?.month;
                        if (m) setSelectedMonth(prev => prev === m ? null : m);
                      },
                    }}
                    animationBegin={200}
                    animationDuration={1300}
                  />
                )}
                {showBars && (
                  <Bar
                    dataKey="profit"
                    maxBarSize={40}
                    animationDuration={850}
                    animationEasing="ease-out"
                    onClick={(entry) => {
                      const m = entry?.month ?? entry?.payload?.month;
                      if (m) setSelectedMonth(prev => prev === m ? null : m);
                    }}
                    cursor="pointer"
                  >
                    {monthlyData.map((m, i) => (
                      <Cell
                        key={i}
                        fill={m.profit >= 0 ? 'url(#ov3BarPos)' : 'url(#ov3BarNeg)'}
                        radius={m.profit >= 0 ? [5, 5, 0, 0] : [0, 0, 5, 5]}
                        stroke={selectedMonth === m.month ? 'rgba(255,255,255,0.55)' : SURFACE}
                        strokeWidth={selectedMonth === m.month ? 1.5 : 1}
                      />
                    ))}
                  </Bar>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Month pills */}
          <div className="ov3-month-strip">
            {monthlyData.map((m) => (
              <button
                key={m.month}
                className={`ov3-month-pill ${selectedMonth === m.month ? 'active' : ''}`}
                onClick={() => setSelectedMonth(selectedMonth === m.month ? null : m.month)}
              >
                <span className="ov3-month-pill-label">{m.label}</span>
                <span className="ov3-month-pill-value" style={{ color: m.profit !== 0 ? valueColor(m.profit) : 'var(--text-dim)' }}>
                  {m.profit !== 0 ? (m.profit >= 0 ? '+' : '−') + '$' + Math.abs(m.profit).toFixed(0) : '—'}
                </span>
              </button>
            ))}
          </div>

          {/* Selected month detail */}
          <AnimatePresence>
            {selectedMonth && monthFlips.length > 0 && (
              <motion.div
                className="ov3-month-detail"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="ov3-month-detail-inner">
                  <div className="ov3-month-detail-title">
                    {MONTH_LABELS[selectedMonth - 1]} {chartYear} — {monthFlips.length} flips
                  </div>
                  {monthFlips.map((r, i) => {
                    const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
                    const p = parseFloat(r.profit) || 0;
                    return (
                      <motion.div key={i} className="ov3-list-row"
                        initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03, duration: 0.25 }}>
                        {img && <img src={img} alt="" className="ov3-skin-img" />}
                        <div className="ov3-list-main">
                          <div className="ov3-list-name">{r.skin?.value}</div>
                          <div className="ov3-list-meta">{r.condition?.value}</div>
                        </div>
                        <div className="ov3-list-num" style={{ color: valueColor(p) }}>
                          {p >= 0 ? '+' : '−'}${Math.abs(p).toFixed(2)}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* ── Monthly Flips ──────────────────────────────── */}
        <motion.div className="panel span-2" variants={riseIn}>
          <div className="panel-header">
            <div>
              <div className="panel-title">Flips — {MONTH_LABELS[flipsMonth - 1]} {chartYear}</div>
              <div className="panel-subtitle">
                {monthFlips.length
                  ? `${monthFlips.length} flip${monthFlips.length > 1 ? 's' : ''} closed · net ${monthNet >= 0 ? '+' : '−'}$${Math.abs(monthNet).toFixed(2)}`
                  : 'No flips closed this month'}
              </div>
            </div>
          </div>
          {monthFlips.length > 0 && (
            <div className="ov3-flips">
              <div className="ov3-flips-head">
                <div>Item</div><div>Condition</div><div>Date</div><div className="ov3-flips-num">Profit</div>
              </div>
              {monthFlips.slice(0, 8).map((r, i) => {
                const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
                const p = parseFloat(r.profit) || 0;
                return (
                  <div key={i} className="ov3-flips-row">
                    <div className="ov3-flips-item">
                      {img && <img src={img} alt="" className="ov3-skin-img" />}
                      <span className="ov3-list-name">{r.skin?.value}</span>
                    </div>
                    <div className="ov3-flips-cond">{r.condition?.value || '—'}</div>
                    <div className="ov3-flips-date">
                      {new Date(r.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <div className="ov3-flips-num" style={{ color: valueColor(p) }}>
                      {p >= 0 ? '+' : '−'}${Math.abs(p).toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* ── Portfolio Mix Donut ───────────────────────── */}
        <motion.div className="panel" variants={riseIn}>
          <div className="panel-header">
            <div>
              <div className="panel-title">Where Your Money Is</div>
              <div className="panel-subtitle">Split by category, based on what you paid</div>
            </div>
          </div>
          <div style={{ padding: 16 }}>
            {categoryData.length === 0 ? (
              <div className="ov3-none">No categorized items</div>
            ) : (
              <>
                <div style={{ position: 'relative' }}>
                  <ResponsiveContainer width="100%" height={210}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%" cy="50%"
                        innerRadius="75%" outerRadius="100%"
                        cornerRadius="50%"
                        paddingAngle={8}
                        minAngle={10}
                        dataKey="value"
                        stroke="none"
                        animationDuration={900}
                        animationEasing="ease-out"
                        onMouseEnter={(_, i) => setHoveredCat(i)}
                        onMouseLeave={() => setHoveredCat(null)}
                      >
                        {categoryData.map((entry, i) => (
                          <Cell
                            key={i}
                            fill={catColors[i]}
                            opacity={hoveredCat === null || hoveredCat === i ? 1 : 0.35}
                            style={{ cursor: 'pointer', transition: 'opacity 0.15s ease' }}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="ov3-donut-center">
                    <AnimatePresence mode="wait" initial={false}>
                      {hoveredCat !== null && categoryData[hoveredCat] ? (
                        <motion.div
                          key={`cat-${hoveredCat}`}
                          className="ov3-donut-inner"
                          initial={{ opacity: 0, scale: 0.4 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.4 }}
                          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                        >
                          <span className="ov3-donut-total" style={{ color: catColors[hoveredCat] }}>
                            <Money value={categoryData[hoveredCat].value} />
                          </span>
                          <span className="ov3-donut-caption">
                            {categoryData[hoveredCat].name} · {(totalCat > 0 ? categoryData[hoveredCat].value / totalCat * 100 : 0).toFixed(0)}%
                          </span>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="total"
                          className="ov3-donut-inner"
                          initial={{ opacity: 0, scale: 0.4 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.4 }}
                          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                        >
                          <span className="ov3-donut-total"><Money value={totalCat} /></span>
                          <span className="ov3-donut-caption">invested</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                <div className="ov3-legend">
                  {categoryData.map((d, i) => {
                    const pct = totalCat > 0 ? d.value / totalCat * 100 : 0;
                    return (
                      <div key={i} className="ov3-legend-row">
                        <span className="ov3-legend-dot" style={{ background: catColors[i] }} />
                        <span className="ov3-legend-name">{d.name}</span>
                        <span className="ov3-legend-pct">{pct.toFixed(0)}%</span>
                        <span className="ov3-legend-val">${d.value.toFixed(0)}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* ── Top Holdings ──────────────────────────────── */}
        <motion.div className="panel" variants={riseIn}>
          <div className="panel-header">
            <div>
              <div className="panel-title">Top Unsold Items</div>
              <div className="panel-subtitle">
                {favOnly ? 'Tracked in your wishlist' : 'Most expensive first'}{holdingsQuery ? ` · "${holdingsQuery}"` : ''}
              </div>
            </div>
            <span className="ov3-header-chip"><Trophy size={15} strokeWidth={2.2} /></span>
          </div>
          <div className="ov3-holdings-toolbar">
            <div className="ov3-search">
              <Search size={15} strokeWidth={2.2} />
              <input
                type="text"
                placeholder="Search holdings…"
                value={holdingsQuery}
                onChange={e => setHoldingsQuery(e.target.value)}
              />
            </div>
            <button
              type="button"
              className={`ov3-fav-toggle ${favOnly ? 'active' : ''}`}
              onClick={() => setFavOnly(v => !v)}
              title={favOnly ? 'Show all holdings' : 'Show only skins you track'}
            >
              <Heart size={15} strokeWidth={2.2} fill={favOnly ? 'currentColor' : 'none'} />
            </button>
          </div>
          <div className="ov3-holdings-grid">
            {topItems.length === 0 ? (
              <div className="ov3-none" style={{ gridColumn: '1 / -1' }}>
                {favOnly && !holdingsQuery
                  ? 'No tracked skins yet. Tap a heart to start recording a skin’s price.'
                  : holdingsQuery ? 'No holdings match' : 'No unsold items'}
              </div>
            ) : topItems.map((row, i) => {
              const img  = findSkinImage(skinsData, row.skin?.value, row.condition?.value);
              const lp   = getLivePrice(livePrices, row);
              const mkt  = lp?.starting_at?.price;
              const buy  = parseFloat(row.buyPrice) || 0;
              const diff = mkt ? mkt - buy : null;
              const type = getItemTypes(row).map(t => settings?.itemTypes?.find(it => it.value === t.value)).find(Boolean);
              const inv    = row.assetId ? inventoryMap?.get(row.assetId) : null;
              const favKey  = holdingKey(row);
              const watched = watchedKeys?.has(favKey) ?? false;
              // Trade-lock countdown: the bar fills as the 7-day lock elapses
              // and sits full/green once the item is tradable. daysLeft is null
              // only when the flag is set with no date to reason from, where we
              // show it locked with an empty bar rather than claim it is free.
              const { locked: stillLocked, until, daysLeft } = getTradeLock(row, inv);
              const locked = stillLocked;
              const lockPct = !stillLocked ? 100
                : daysLeft === null ? 0
                : Math.round((TRADE_LOCK_DAYS - Math.min(daysLeft, TRADE_LOCK_DAYS)) / TRADE_LOCK_DAYS * 100);
              const lockLabel = !stillLocked ? 'Tradable now'
                : daysLeft === null ? 'Trade locked'
                : `Trade locked · unlocks in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
              return (
                <motion.div key={i} className="ov3-hcard idm-clickable"
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.06 }}
                  role="button" tabIndex={0} title="Open details"
                  onClick={() => setDetail({ row, img })}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail({ row, img }); } }}>
                  <div className="ov3-hcard-rank">{String(i + 1).padStart(2, '0')}</div>
                  <button
                    type="button"
                    className={`ov3-hcard-fav ${watched ? 'active' : ''}`}
                    onClick={e => { e.stopPropagation(); onToggleWatch?.(row.skin?.value, row.condition?.value); }}
                    title={watched
                      ? 'Tracked in your wishlist. Click to stop tracking.'
                      : 'Track this skin’s price in your wishlist'}
                  >
                    <Heart size={13} strokeWidth={2.4} fill={watched ? 'currentColor' : 'none'} />
                  </button>
                  {locked && (
                    <div className="ov3-hcard-lock" title={until ? `Locked until ${new Date(until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Trade locked'}>
                      <Lock size={12} strokeWidth={2.4} />
                    </div>
                  )}
                  <div className="ov3-hcard-stage">
                    {img && <img src={img} alt="" className="ov3-hcard-img" />}
                  </div>
                  <div className="ov3-hcard-body">
                    <div className="ov3-hcard-name">{row.skin?.value}</div>
                    <div className="ov3-hcard-meta">
                      <span>{row.condition?.value}</span>
                      {type && <span className="ov3-type-badge" style={{ color: type.color, background: type.color + '2e' }}>{type.label}</span>}
                    </div>
                  </div>
                  <div className="ov3-hcard-footer">
                    <span className="ov3-hcard-price">${buy.toFixed(2)}</span>
                    {diff !== null && (
                      <span className="ov3-list-delta" style={{ color: valueColor(diff) }}>
                        {diff >= 0 ? '+' : '−'}${Math.abs(diff).toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div className="ov3-hcard-share" title={lockLabel}>
                    <motion.div
                      className={`ov3-share-fill ${stillLocked ? 'locked' : ''}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.max(3, lockPct)}%` }}
                      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.07 }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* ── Recent Activity ───────────────────────────── */}
        <motion.div className="panel" variants={riseIn}>
          <div className="panel-header">
            <div>
              <div className="panel-title">Recent Activity</div>
              <div className="panel-subtitle">Latest items added</div>
            </div>
            <span className="ov3-header-chip"><History size={15} strokeWidth={2.2} /></span>
          </div>
          <div className="ov3-timeline">
            {recent.map((row, i) => {
              const img    = findSkinImage(skinsData, row.skin?.value, row.condition?.value);
              const sold   = parseFloat(row.sellPrice) > 0;
              const profit = parseFloat(row.profit) || 0;
              return (
                <div key={i} className="ov3-timeline-row">
                  <div className="ov3-timeline-rail">
                    <span className={`ov3-timeline-dot ${sold ? (profit >= 0 ? 'green' : 'red') : 'amber'}`} />
                    {i < recent.length - 1 && <span className="ov3-timeline-line" />}
                  </div>
                  <div className="ov3-timeline-card">
                    {img && <img src={img} alt="" className="ov3-skin-img" />}
                    <div className="ov3-list-main">
                      <div className="ov3-list-name">{row.skin?.value}</div>
                      <div className="ov3-list-meta">
                        {new Date(row.timestamp).toLocaleDateString()} · {row.condition?.value}
                      </div>
                    </div>
                    <div className="ov3-list-right">
                      {sold ? (
                        <>
                          <div className="ov3-list-num" style={{ color: valueColor(profit) }}>
                            {profit >= 0 ? '+' : '−'}${Math.abs(profit).toFixed(2)}
                          </div>
                          <div className="ov3-list-tag">sold</div>
                        </>
                      ) : (
                        <>
                          <div className="ov3-list-num" style={{ color: 'var(--amber)' }}>
                            ${(parseFloat(row.buyPrice) || 0).toFixed(2)}
                          </div>
                          <div className="ov3-list-tag">holding</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* ── Category Towers (hand-drawn cylinders) ────── */}
        <motion.div className="panel" variants={riseIn}>
          <div className="panel-header">
            <div>
              <div className="panel-title">Category Towers</div>
              <div className="panel-subtitle">Invested value per type</div>
            </div>
          </div>
          <div style={{ padding: '20px 18px 16px' }}>
            {categoryData.length === 0 ? (
              <div className="ov3-none">No categorized items</div>
            ) : (
              <div className="ov3-cyl-track">
                {categoryData.map((d, i) => (
                  <CylinderColumn
                    key={d.name}
                    fill={catColors[i]}
                    label={d.name}
                    value={d.value}
                    pct={totalCat > 0 ? d.value / totalCat * 100 : 0}
                    delay={0.15 + i * 0.09}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* ── Trade Lock Queue ───────────────────────────── */}
        <motion.div className="panel" variants={riseIn}>
          <div className="panel-header">
            <div>
              <div className="panel-title">Trade Lock Queue</div>
              <div className="panel-subtitle">Value tied up in trade-protected items</div>
            </div>
            <span className="ov3-header-chip"><Lock size={15} strokeWidth={2.2} /></span>
          </div>
          <div className="ov3-lockq">
            {(() => {
              const total = stats.marketValue || stats.unsoldCost || 1;
              const pct = Math.min(100, (stats.lockedCost ?? 0) / total * 100);
              return (
                <>
                  <div className="ov3-lockq-row">
                    <div>
                      <span className="ov3-lockq-value"><Money value={stats.lockedCost ?? 0} /></span>
                      <span className="ov3-lockq-of"> locked of <Money value={total} /></span>
                    </div>
                    <div className="ov3-lockq-pct">{pct.toFixed(1)}%</div>
                  </div>
                  <div className="ov3-lockq-track">
                    <motion.div
                      className="ov3-lockq-fill"
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
                    />
                  </div>
                  <div className="ov3-lockq-sub">
                    {stats.lockedCount
                      ? `${stats.lockedCount} locked item${stats.lockedCount > 1 ? 's' : ''} · ${stats.unlock7} unlocking within 7 days`
                      : 'No trade-locked items'}
                  </div>
                </>
              );
            })()}
          </div>
        </motion.div>

        {/* ── Market Meters ──────────────────────────────── */}
        <motion.div className="panel" variants={riseIn}>
          <div className="panel-header">
            <div>
              <div className="panel-title">How You're Doing</div>
              <div className="panel-subtitle">
                {vsMarket ? 'Quick checks on your trading, and how it compares to the market' : 'Three quick checks on your own trading'}
              </div>
            </div>
            <span className="ov3-header-chip"><Activity size={15} strokeWidth={2.2} /></span>
          </div>
          <div className="ov3-meters">
            {meters.map(m => <MeterBar key={m.name} {...m} />)}
          </div>
        </motion.div>

        {/* ── Trading Performance ───────────────────────── */}
        {perf && (
          <motion.div className="panel span-2" variants={riseIn}>
            <div className="panel-header">
              <div>
                <div className="panel-title">Your Track Record</div>
                <div className="panel-subtitle">
                  From {perf.count} completed sale{perf.count !== 1 ? 's' : ''} · {perf.wins} made money
                </div>
              </div>
              <span className="ov3-header-chip"><Target size={15} strokeWidth={2.2} /></span>
            </div>
            <div className="ov3-perf">
              <WinRing pct={perf.winRate} />
              <div className="ov3-perf-stat">
                <span className="ov3-stat-label">Avg profit per sale</span>
                <strong style={{ color: valueColor(perf.avg) }}>
                  <Money value={perf.avg} signed />
                </strong>
              </div>
              <div className="ov3-perf-trade">
                <span className="ov3-stat-label">Best sale</span>
                <div className="ov3-perf-trade-row">
                  {(() => { const img = findSkinImage(skinsData, perf.best.skin?.value, perf.best.condition?.value);
                    return img ? <img src={img} alt="" className="ov3-skin-img" /> : null; })()}
                  <div className="ov3-list-main">
                    <div className="ov3-list-name">{perf.best.skin?.value}</div>
                  </div>
                  <span className="ov3-list-num" style={{ color: valueColor(bestP) }}>
                    {bestP >= 0 ? '+' : '−'}${Math.abs(bestP).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="ov3-perf-trade">
                <span className="ov3-stat-label">Worst sale</span>
                <div className="ov3-perf-trade-row">
                  {(() => { const img = findSkinImage(skinsData, perf.worst.skin?.value, perf.worst.condition?.value);
                    return img ? <img src={img} alt="" className="ov3-skin-img" /> : null; })()}
                  <div className="ov3-list-main">
                    <div className="ov3-list-name">{perf.worst.skin?.value}</div>
                  </div>
                  <span className="ov3-list-num" style={{ color: valueColor(worstP) }}>
                    {worstP >= 0 ? '+' : '−'}${Math.abs(worstP).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Portfolio Value History ───────────────────── */}
        {portfolioHistory.length >= 2 && (
          <motion.div className="panel span-2" variants={riseIn}>
            <div className="panel-header">
              <div>
                <div className="panel-title">Value Over Time</div>
                <div className="panel-subtitle">
                  What you paid vs what it's worth now · {portfolioHistory.length} days tracked
                </div>
              </div>
            </div>
            <div className="chart-shell" style={{ padding: '16px 8px 8px' }}>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={portfolioHistory} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ov3MvGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#818cf8" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#818cf8" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="ov3CbGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#f59e0b" stopOpacity={0.16} />
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: AXIS_TICK, fontSize: 14 }} axisLine={false} tickLine={false}
                    interval="preserveStartEnd" tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fill: AXIS_TICK, fontSize: 14 }} axisLine={false} tickLine={false}
                    tickFormatter={v => `$${v}`} width={52} />
                  <Tooltip
                    contentStyle={{ background: '#0e1826', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, fontSize: '0.95rem' }}
                    formatter={(v, name) => [`$${parseFloat(v).toFixed(2)}`, name]} />
                  <Legend iconType="plainline" wrapperStyle={{ fontSize: '0.92rem', paddingTop: 6 }} />
                  <Area type="monotone" name="Cost Basis" dataKey="costBasis" stroke="#f59e0b" strokeWidth={1.5}
                    fill="url(#ov3CbGrad)" dot={false} strokeDasharray="4 3" />
                  <Area type="monotone" name="Market Value" dataKey="marketValue" stroke="#818cf8" strokeWidth={2}
                    fill="url(#ov3MvGrad)" dot={false} activeDot={{ fill: '#818cf8', stroke: SURFACE, strokeWidth: 2, r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}

        {detail && (
          <ItemDetailModal
          skinsData={skinsData}
            variant="classic"
            kind={parseFloat(detail.row.sellPrice) > 0 ? 'sold' : 'item'}
            name={detail.row.skin?.value}
            condition={detail.row.condition?.value}
            image={detail.img}
            history={detail.row.priceHistory}
            livePrices={livePrices}
            csfloatPrices={csfloatPrices}
            row={detail.row}
            onClose={() => setDetail(null)}
          />
        )}

      </div>
    </motion.div>
  );
}
